import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { setActionPayload, setActionType } from "../../../features/authorizationSlice";
import { ACTION_TYPES } from "../../../app/actionTypes";
import {
  capitalizeFirstLetters,
  formatAccount,
} from "../../../features/ValueFormater";
import { exportToExcel } from "../../../utils/functions";
import useNewSalesTransactionsAPI from "../../api/salesTransactionsAPI";
import DataTable from "../Table";
import TableFilters from "../Common/TableFilters";
import TableExportButton from "../Common/TableExportButton";
import FloatingActionMenu from "../Common/FloatingActionMenu";
import UtcToLocal from "../UtcToLocal";
import HTMLtoPDFGenerator from "../ReceiptDownload/HTMLtoPDFGenerator";
import { StatusBadge } from "../Table/cells";
import useLoadMoreList from "../../hooks/useLoadMoreList";
import {
  createDefaultDateRange,
  fromPickerRange,
  toPickerRange,
} from "../../utils/dateRange";

const MERCHANT_TRANSACTION_OPTIONS = [
  { value: "All", label: "All Transactions" },
  { value: "QRPayment", label: "QR Payment" },
  { value: "RemotePayment", label: "Remote Payment" },
];

function normalizeSalesRow(row, currencyId) {
  const status = row?.Status || "";
  const isReceiptOnlyStatus = isReceiptOnlySalesStatus(status);
  const receipt = row?.Receipt || null;

  return {
    id:
      row?.Row ||
      row?.TransactionID ||
      [
        row?.Transaction || "-",
        row?.Reference || "-",
        row?.PaymentDate || "-",
        row?.Method || "-",
        status || "-",
      ].join("|"),
    transaction: row?.Transaction || "-",
    reference: row?.Reference || "-",
    paymentDate: row?.PaymentDate,
    method: row?.Method || "-",
    branch: row?.Branch || "-",
    till: row?.Till || "-",
    client: row?.Client || "-",
    amount: Number(row?.Amount ?? 0),
    commission: row?.Commission ?? 0,
    rebate: row?.Rebate ?? 0,
    sign: row?.Sign || "",
    status,
    currencyId,
    receipt,
    isReversible: Boolean(row?.IsReversible),
    isDownloadReceipt: Boolean(row?.IsDownloadReceipt) || Boolean(isReceiptOnlyStatus && receipt),
    isReceiptOnlyStatus,
    original: row,
  };
}

function isReceiptOnlySalesStatus(status) {
  const normalized = String(status || "").trim().toLowerCase();
  return normalized.includes("reserved") || normalized.includes("reversed");
}

function normalizeExportRow(row, currencyId) {
  const paymentDate = row?.PaymentDate ? new Date(row.PaymentDate) : null;
  const formattedDate =
    paymentDate && !Number.isNaN(paymentDate.getTime())
      ? paymentDate.toLocaleDateString()
      : row?.PaymentDate || "";
  const formattedTime =
    paymentDate && !Number.isNaN(paymentDate.getTime())
      ? paymentDate.toLocaleTimeString()
      : "";

  return {
    Transaction: row?.Transaction,
    Reference: row?.Reference,
    PaymentDate: formattedDate,
    PaymentTime: formattedTime,
    Type: row?.Method,
    Branch: row?.Branch,
    Till: row?.Till,
    Client: capitalizeFirstLetters(String(row?.Client || "")),
    Currency: currencyId === 1 ? "USD" : "LBP",
    Amount: row?.Sign === "-" ? -Number(row?.Amount ?? 0) : Number(row?.Amount ?? 0),
    Commission:
      Number(row?.Commission ?? 0) === 0
        ? 0
        : row?.Sign === "-"
          ? -Number(row?.Commission ?? 0)
          : Number(row?.Commission ?? 0),
    Rebate:
      Number(row?.Rebate ?? 0) === 0
        ? 0
        : row?.Sign === "-"
          ? -Number(row?.Rebate ?? 0)
          : Number(row?.Rebate ?? 0),
    Status: /^\d/.test(String(row?.Status || ""))
      ? ""
      : capitalizeFirstLetters(String(row?.Status || "")),
  };
}

export default function MerchantSalesHistory({
  currencyId,
  onBalancesChange,
  onOpenQrAfterGenerate,
}) {
  const dispatch = useDispatch();
  const {
    GetSalesTransactions,
    GetSalesTransactionsExport,
    GetAvailableSalesAndBalance,
    GetBranchesTillsByMerchant,
    branchesTillsData,
    exportLoading,
  } = useNewSalesTransactionsAPI();
  const [dateRange, setDateRange] = useState(() => createDefaultDateRange());
  const [transactionType, setTransactionType] = useState("All");
  const [searchDraft, setSearchDraft] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [listError, setListError] = useState("");
  const [selectedBranchName, setSelectedBranchName] = useState("");
  const [selectedTillName, setSelectedTillName] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const getTransactionsRef = useRef(GetSalesTransactions);
  const getBalancesRef = useRef(GetAvailableSalesAndBalance);
  const getBranchesRef = useRef(GetBranchesTillsByMerchant);

  getTransactionsRef.current = GetSalesTransactions;
  getBalancesRef.current = GetAvailableSalesAndBalance;
  getBranchesRef.current = GetBranchesTillsByMerchant;

  const {
    items: salesTransactionItems,
    isInitialLoading: salesLoading,
    isLoadingMore,
    hasMore,
    error: salesError,
    loadMoreError,
    loadMore,
    refresh: fetchTransactions,
  } = useLoadMoreList({
    apiFunction: ({ lastDate }) =>
      getTransactionsRef.current(
        currencyId,
        lastDate,
        dateRange.from,
        dateRange.to,
        transactionType,
        selectedBranchName,
        selectedTillName,
        false,
      ),
    cursorKey: "lastDate",
    getCursor: (row) => row?.RowDate || row?.rowDate || null,
    getItemId: (row) =>
      row?.Row ||
      row?.TransactionID ||
      row?.Transaction ||
      [
        row?.Reference || "-",
        row?.PaymentDate || "-",
        row?.Method || "-",
        row?.Status || "-",
      ].join("|"),
    resetKey: [
      currencyId,
      dateRange.from,
      dateRange.to,
      transactionType,
      selectedBranchName,
      selectedTillName,
    ].join("|"),
  });

  const rows = useMemo(
    () =>
      (Array.isArray(salesTransactionItems) ? salesTransactionItems : []).map((row) =>
        normalizeSalesRow(row, currencyId),
      ),
    [currencyId, salesTransactionItems],
  );

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      [
        row.transaction,
        row.reference,
        row.paymentDate,
        row.method,
        row.branch,
        row.till,
        row.client,
        row.amount,
        row.status,
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .some((value) => value.includes(query)),
    );
  }, [rows, searchQuery]);

  const uniqueBranchNames = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(branchesTillsData) ? branchesTillsData : [])
            .map((branch) => branch?.BranchName)
            .filter(Boolean),
        ),
      ),
    [branchesTillsData],
  );

  const tillOptions = useMemo(
    () =>
      (Array.isArray(branchesTillsData) ? branchesTillsData : [])
        .filter((item) =>
          selectedBranchName ? item?.BranchName === selectedBranchName : true,
        )
        .map((item) => item?.TillName)
        .filter(Boolean),
    [branchesTillsData, selectedBranchName],
  );

  const allRowsSelected =
    filteredRows.length > 0 && selectedRowKeys.length === filteredRows.length;
  const pickerRange = useMemo(() => toPickerRange(dateRange), [dateRange]);

  const closeMenu = useCallback(() => {
    setOpenMenuId(null);
  }, []);

  useEffect(() => {
    getBranchesRef.current().catch(() => {});
    getBalancesRef.current()
      .then((balances) => {
        onBalancesChange?.(balances);
      })
      .catch(() => {});
  }, [onBalancesChange]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(searchDraft);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchDraft]);

  useEffect(() => {
    setSelectedRowKeys([]);
    closeMenu();
  }, [
    currencyId,
    dateRange.from,
    dateRange.to,
    selectedBranchName,
    selectedTillName,
    transactionType,
    closeMenu,
  ]);

  useEffect(() => {
    if (salesError) {
      setListError("Unable to load sales transactions right now.");
    } else {
      setListError("");
    }
  }, [salesError]);

  const handleReverse = useCallback(
    (transactionId) => {
      dispatch(setActionType(ACTION_TYPES.REVERSE_TRANSACTION));
      dispatch(setActionPayload({ TransactionID: transactionId }));
      onOpenQrAfterGenerate?.();
    },
    [dispatch, onOpenQrAfterGenerate],
  );

  const handleExport = useCallback(async () => {
    const exportRows = await GetSalesTransactionsExport(
      currencyId,
      dateRange.from,
      dateRange.to,
      transactionType,
    );

    exportToExcel({
      excelData: exportRows.map((row) => normalizeExportRow(row, currencyId)),
      filename: `Transactions-${new Date(dateRange.to).getTime()}`,
    });
  }, [GetSalesTransactionsExport, currencyId, dateRange.from, dateRange.to, transactionType]);

  const columns = useMemo(
    () => [
      {
        id: "selection",
        label: "",
        header: (
          <input
            type="checkbox"
            checked={allRowsSelected}
            onChange={() => {
              setSelectedRowKeys((current) =>
                current.length === filteredRows.length ? [] : filteredRows.map((row) => row.id),
              );
            }}
            aria-label="Select all sales transactions"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRowKeys.includes(row.id)}
            onChange={(event) => {
              event.stopPropagation();
              setSelectedRowKeys((current) =>
                current.includes(row.id)
                  ? current.filter((item) => item !== row.id)
                  : [...current, row.id],
              );
            }}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select transaction ${row.transaction}`}
          />
        ),
      },
      {
        header: "Transaction",
        accessor: "transaction",
      },
      {
        header: "Reference",
        accessor: "reference",
      },
      {
        header: "Payment Date",
        accessor: "paymentDate",
      },
      {
        header: "Payment Time",
        accessor: "paymentDate",
        cell: ({ value }) => (
          <UtcToLocal
            utcTimestamp={value}
            options={{ hour: "numeric", minute: "numeric", hour12: true }}
          />
        ),
      },
      {
        header: "Type",
        accessor: "method",
      },
      {
        header: "Branch",
        accessor: "branch",
      },
      {
        header: "Till",
        accessor: "till",
      },
      {
        header: "Client",
        accessor: "client",
        cell: ({ value }) => capitalizeFirstLetters(String(value || "-")),
      },
      {
        header: `Amount (${currencyId === 1 ? "$" : "L.L"})`,
        accessor: "amount",
        cell: ({ row }) => {
          const colorClass =
            row.sign === "+" ? "is-positive" : row.sign === "-" ? "is-negative" : "";

          return (
            <span className={`new-data-table__amount ${colorClass}`}>
              {row.sign === "+" ? "+" : row.sign === "-" ? "-" : ""}
              {formatAccount(Math.abs(Number(row.amount ?? 0)), currencyId)}
            </span>
          );
        },
      },
      {
        header: "Commission",
        accessor: "commission",
      },
      {
        header: "Rebate",
        accessor: "rebate",
      },
      {
        header: "Status",
        accessor: "status",
        cell: ({ value, isUtcDateValue, renderTableCellValue }) =>
          isUtcDateValue(value)
            ? renderTableCellValue(value)
            : <StatusBadge value={capitalizeFirstLetters(String(value || ""))} />,
      },
      {
        id: "actions",
        header: "",
        align: "right",
        cell: ({ row }) => {
          const showReceiptOnly = row.isReceiptOnlyStatus && row.isDownloadReceipt;
          const showReverseAction = row.isReversible && !showReceiptOnly;

          return showReverseAction || row.isDownloadReceipt ? (
            <FloatingActionMenu
              id={row.id}
              activeId={openMenuId}
              onActiveIdChange={setOpenMenuId}
              className="new-statement-of-account__menu"
              triggerClassName="new-statement-of-account__menu-trigger"
              panelClassName="new-statement-of-account__menu-panel"
              ariaLabel={`Open transaction ${row.transaction} actions`}
              renderTrigger={() => <FontAwesomeIcon icon={faEllipsisVertical} />}
            >
              {showReverseAction ? (
                <button
                  type="button"
                  className="new-statement-of-account__menu-item"
                  onClick={() => {
                    closeMenu();
                    handleReverse(row.transaction);
                  }}
                >
                  Reverse Transaction
                </button>
              ) : null}

              {row.isDownloadReceipt ? (
                <div className="new-statement-of-account__menu-item is-plain">
                  <HTMLtoPDFGenerator data={row.receipt} />
                </div>
              ) : null}
            </FloatingActionMenu>
          ) : null;
        },
      },
    ],
    [
      allRowsSelected,
      currencyId,
      filteredRows,
      handleReverse,
      closeMenu,
      openMenuId,
      selectedRowKeys,
    ],
  );

  return (
    <section className="new-statement-of-account__history is-merchant">
      <div className="new-statement-of-account__toolbar">
        <TableFilters
          className="new-statement-of-account__filters is-merchant"
          dateRange={pickerRange}
          onDateRangeChange={(range) => {
            const nextRange = fromPickerRange(range, createDefaultDateRange());
            if (nextRange) setDateRange(nextRange);
          }}
          onSearchChange={setSearchDraft}
          searchValue={searchDraft}
          showDateRange
          selects={[
            {
              key: "transaction-type",
              className: "new-statement-of-account__select-filter",
              options: MERCHANT_TRANSACTION_OPTIONS,
              value: transactionType,
              onChange: setTransactionType,
            },
            ...(uniqueBranchNames.length > 0
              ? [
                  {
                    key: "branch",
                    className: "new-statement-of-account__select-filter",
                    options: [
                      { value: "", label: "All Branches" },
                      ...uniqueBranchNames.map((branchName) => ({
                        value: branchName,
                        label: branchName,
                      })),
                    ],
                    value: selectedBranchName,
                    onChange: (value) => {
                      setSelectedBranchName(value);
                      setSelectedTillName("");
                    },
                  },
                ]
              : []),
            ...(tillOptions.length > 0
              ? [
                  {
                    key: "till",
                    className: "new-statement-of-account__select-filter",
                    options: [
                      { value: "", label: "All Tills" },
                      ...tillOptions.map((tillName) => ({
                        value: tillName,
                        label: tillName,
                      })),
                    ],
                    value: selectedTillName,
                    onChange: setSelectedTillName,
                  },
                ]
              : []),
          ]}
          customActions={
            <TableExportButton
              loading={exportLoading}
              onExport={handleExport}
            />
          }
        />
      </div>

      {/* <div className="new-statement-of-account__summary">
        <span>
          {totalCount || filteredRows.length} transaction
          {(totalCount || filteredRows.length) === 1 ? "" : "s"}
        </span>
        <span>{selectedRowKeys.length} selected</span>
      </div> */}

      <div className="new-statement-of-account__table is-full-width">
        <DataTable
          columns={columns}
          data={filteredRows}
          loading={salesLoading}
          error={listError}
          errorMessage="Unable to load data right now."
          onRetry={() => {
            setListError("");
            fetchTransactions().catch(() => {
              setListError("Unable to load sales transactions right now.");
            });
          }}
          rowKey="id"
          rowClickable={false}
          emptyMessage="No transactions available."
          showLoadMore
          hasMore={hasMore}
          loadMoreLoading={isLoadingMore}
          loadMoreDisabled={salesLoading || isLoadingMore}
          loadMoreError={loadMoreError}
          onLoadMore={loadMore}
        />
      </div>

    </section>
  );
}
